import Drawer from "@mui/material/Drawer";
import React, { useEffect, useState, useCallback } from "react";
import { Box, Skeleton, Alert } from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../hooks/redux";
import { getTranslationRecordDetails } from "../../services/translationApi";
import DetailsSheet from "./DetailsSheet";
import { buildViewModel } from "./logic/viewModel/buildViewModel";
import { selectDetailsDrawerOpen, selectDetailsDrawerTranslationId } from "../../store/selectors/uiSelectors";
import { closeTranslationDetails, setDetailsViewModel } from "../../store/slices/uiSlice";
import { selectEditingLoading, selectApprovalLoading } from "../../store/selectors/translationsSelectors";

const DetailsDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectDetailsDrawerOpen);
  const translationRecordId = useAppSelector(selectDetailsDrawerTranslationId);
  const [translationDetail, setTranslationDetail] = useState<TranslationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentUser = useAppSelector((state) => state.userProfile.data);
  
  const editingLoading = useAppSelector(selectEditingLoading);
  const approvalLoading = useAppSelector(selectApprovalLoading);
  
  const prevEditingLoadingRef = React.useRef(editingLoading);
  const prevApprovalLoadingRef = React.useRef(approvalLoading);

  const fetchTranslationDetails = useCallback(
    async (id: string) => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getTranslationRecordDetails({ translationRecordId: id, language: 'es' });
        setTranslationDetail(data);
      } catch (err: any) {
        setError(err?.message || 'Failed to fetch translation details');
        setTranslationDetail(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Fetch details when translationRecordId changes and drawer is open
  useEffect(() => {
    if (isOpen && translationRecordId) {
      fetchTranslationDetails(translationRecordId);
    } else if (!isOpen) {
      // Reset state when drawer closes
      setTranslationDetail(null);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, translationRecordId, fetchTranslationDetails]);

  useEffect(() => {
    if (translationRecordId && prevEditingLoadingRef.current && !editingLoading) {
      fetchTranslationDetails(translationRecordId);
    }
    prevEditingLoadingRef.current = editingLoading;
  }, [editingLoading, translationRecordId, fetchTranslationDetails]);

  useEffect(() => {
    if (translationRecordId && prevApprovalLoadingRef.current && !approvalLoading) {
      fetchTranslationDetails(translationRecordId);
    }
    prevApprovalLoadingRef.current = approvalLoading;
  }, [approvalLoading, translationRecordId, fetchTranslationDetails]);

  // Compute and store view model in Redux
  useEffect(() => {
    if (currentUser && translationDetail) {
      const viewModel = buildViewModel(
        { role: currentUser.role, userDisplayName: currentUser.userDisplayName },
        translationDetail
      );
      dispatch(setDetailsViewModel(viewModel));
    } else {
      dispatch(setDetailsViewModel(null));
    }
  }, [currentUser, translationDetail, dispatch]);

  const handleClose = () => {
    dispatch(closeTranslationDetails());
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      variant="persistent"
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            boxShadow: 'none',
            borderLeft: '1px solid #e0e0e0'
          },
        },
      }}
    >
      {loading && (
        <Box sx={styles.root}>
          <Skeleton variant="text" width={180} height={32} sx={styles.skeletonTitle} />
          <Skeleton variant="rectangular" width="100%" height={40} sx={styles.skeletonRect} />
          <Skeleton variant="text" width={120} height={24} sx={styles.skeletonText} />
          <Skeleton variant="text" width={120} height={24} sx={styles.skeletonText} />
          <Skeleton variant="rectangular" width="100%" height={80} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={styles.root}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && translationDetail && (
        <DetailsSheet 
          details={translationDetail} 
        />
      )}
    </Drawer>
  )
}

const styles = {
  root: {
    width: 600,
    bgcolor: '#fff',
    boxShadow: 'none',
    p: 3,
    borderRadius: 0,
    position: 'relative',
  },
  skeletonTitle: {
    mb: 4,
  },
  skeletonRect: {
    mb: 3,
  },
  skeletonText: {
    mb: 2,
  },
}

export default DetailsDrawer;
